import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
} from "@mui/material";
import axios from "axios";
import { CONFIG } from "../config";
import { downloadGonogoExcel } from "../utils/gonogoExport";
import "../css/Gonogo.css";

const TOTAL_TRIALS = 24;
const GREEN_COUNT = 14;
const FIXATION_MS = 500;
const STIMULUS_MS = 2000;
const ITI_MS = 400;

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function buildTrialSequence() {
  const colors = [
    ...Array(GREEN_COUNT).fill("green"),
    ...Array(TOTAL_TRIALS - GREEN_COUNT).fill("red"),
  ];
  return shuffle(colors).map((color, index) => ({
    trial_number: index + 1,
    color,
  }));
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function computeSummary(trials) {
  const omission_errors = trials.filter(
    (t) => t.stimulus_color === "green" && t.action_taken === "none"
  ).length;
  const commission_errors = trials.filter(
    (t) => t.stimulus_color === "red" && t.action_taken === "pressed"
  ).length;
  const correct_hits = trials.filter((t) => t.is_correct).length;
  const reactionTimes = trials
    .filter((t) => t.stimulus_color === "green" && t.reaction_time_ms != null)
    .map((t) => t.reaction_time_ms);
  const mean_reaction_time_ms =
    reactionTimes.length > 0
      ? Math.round(
          reactionTimes.reduce((sum, rt) => sum + rt, 0) / reactionTimes.length
        )
      : 0;

  return {
    total_trials: trials.length,
    correct_hits,
    omission_errors,
    commission_errors,
    mean_reaction_time_ms,
  };
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("es-MX", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function ResultsPanel({
  patientName,
  summary,
  trials,
  saveStatus,
  readOnly,
  onRetry,
  onBackMenu,
}) {
  return (
    <div className="gonogo-card">
      <h1>Resultados</h1>
      <p>
        Paciente: <strong>{patientName}</strong>
      </p>

      <div className="gonogo-results-grid">
        <div className="gonogo-metric">
          <div className="gonogo-metric__value">
            {summary.mean_reaction_time_ms}
          </div>
          <div className="gonogo-metric__label">
            Media de tiempo de reacción (ms)
          </div>
        </div>
        <div className="gonogo-metric">
          <div className="gonogo-metric__value">{summary.omission_errors}</div>
          <div className="gonogo-metric__label">Errores de omisión</div>
        </div>
        <div className="gonogo-metric">
          <div className="gonogo-metric__value">{summary.correct_hits}</div>
          <div className="gonogo-metric__label">Respuestas correctas</div>
        </div>
        <div className="gonogo-metric">
          <div className="gonogo-metric__value">{summary.commission_errors}</div>
          <div className="gonogo-metric__label">
            Errores por comisión (rojo + espacio)
          </div>
        </div>
      </div>

      {!readOnly && (
        <p
          className={`gonogo-save-status ${
            saveStatus === "ok"
              ? "gonogo-save-status--ok"
              : saveStatus === "error"
                ? "gonogo-save-status--error"
                : ""
          }`}
        >
          {saveStatus === "saving" &&
            "Guardando resultados en la base de datos…"}
          {saveStatus === "ok" && "Resultados guardados correctamente."}
          {saveStatus === "error" &&
            "No se pudieron guardar los resultados. Verifica que el backend esté en ejecución."}
        </p>
      )}

      <table className="gonogo-trials-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Color</th>
            <th>Acción</th>
            <th>Correcto</th>
            <th>T. reacción (ms)</th>
          </tr>
        </thead>
        <tbody>
          {trials.map((t) => (
            <tr key={t.trial_number}>
              <td>{t.trial_number}</td>
              <td>{t.stimulus_color === "green" ? "Verde" : "Rojo"}</td>
              <td>{t.action_taken === "pressed" ? "Espacio" : "—"}</td>
              <td>{t.is_correct ? "Sí" : "No"}</td>
              <td>{t.reaction_time_ms ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="gonogo-actions">
        {!readOnly && onRetry && (
          <Button variant="contained" color="primary" onClick={onRetry}>
            Realizar otra prueba
          </Button>
        )}
        <Button variant="outlined" onClick={onBackMenu}>
          Volver al menú
        </Button>
      </div>
    </div>
  );
}

export default function Gonogo() {
  const [phase, setPhase] = useState("menu");
  const [patientName, setPatientName] = useState("");
  const [patientError, setPatientError] = useState(false);
  const [readOnlyResults, setReadOnlyResults] = useState(false);

  const [stimulus, setStimulus] = useState(null);
  const [showFixation, setShowFixation] = useState(false);
  const [trialProgress, setTrialProgress] = useState({
    current: 0,
    total: TOTAL_TRIALS,
  });

  const [trials, setTrials] = useState([]);
  const [summary, setSummary] = useState(null);
  const [saveStatus, setSaveStatus] = useState("idle");

  const [savedResults, setSavedResults] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const testAreaRef = useRef(null);
  const runningRef = useRef(false);
  const testStartedRef = useRef(false);

  const runSingleTrial = useCallback(async (trial) => {
    setShowFixation(true);
    setStimulus(null);
    await sleep(FIXATION_MS);

    const record = await new Promise((resolve) => {
      let pressed = false;
      let reactionTimeMs = null;
      let finished = false;
      const onset = performance.now();

      const finishTrial = () => {
        if (finished) return;
        finished = true;
        window.removeEventListener("keydown", onKeyDown);
        clearTimeout(timeoutId);
        setStimulus(null);

        const action_taken = pressed ? "pressed" : "none";
        let is_correct = false;
        if (trial.color === "green" && pressed) is_correct = true;
        if (trial.color === "red" && !pressed) is_correct = true;

        resolve({
          trial_number: trial.trial_number,
          stimulus_color: trial.color,
          action_taken,
          is_correct,
          reaction_time_ms:
            trial.color === "green" && pressed ? reactionTimeMs : null,
        });
      };

      const onKeyDown = (e) => {
        if (e.code !== "Space" && e.key !== " ") return;
        e.preventDefault();
        if (!pressed) {
          pressed = true;
          reactionTimeMs = Math.round(performance.now() - onset);
          // Verde correcto o error (rojo + espacio): pasar al siguiente de inmediato
          finishTrial();
        }
      };

      window.addEventListener("keydown", onKeyDown);
      setShowFixation(false);
      setStimulus(trial.color);

      const timeoutId = setTimeout(finishTrial, STIMULUS_MS);
    });

    await sleep(ITI_MS);
    return record;
  }, []);

  const runAllTrials = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;

    const sequence = buildTrialSequence();
    const results = [];

    for (let i = 0; i < sequence.length; i += 1) {
      setTrialProgress({ current: i + 1, total: sequence.length });
      const record = await runSingleTrial(sequence[i]);
      results.push(record);
    }

    const metrics = computeSummary(results);
    setTrials(results);
    setSummary(metrics);
    runningRef.current = false;
    setReadOnlyResults(false);
    setPhase("results");
  }, [runSingleTrial]);

  useEffect(() => {
    if (phase !== "test") {
      testStartedRef.current = false;
      return;
    }
    if (testStartedRef.current) return;
    testStartedRef.current = true;
    testAreaRef.current?.focus();
    runAllTrials();
  }, [phase, runAllTrials]);

  useEffect(() => {
    if (phase !== "history") return;

    const load = async () => {
      setHistoryLoading(true);
      setHistoryError("");
      try {
        const res = await axios.get(
          `${CONFIG.BACKEND_URL}/backend/reaction-tests`
        );
        setSavedResults(res.data || []);
      } catch (err) {
        console.error("Error al cargar historial:", err);
        setHistoryError(
          "No se pudieron cargar los resultados. Verifica que el backend esté en ejecución."
        );
        setSavedResults([]);
      } finally {
        setHistoryLoading(false);
      }
    };

    load();
  }, [phase]);

  useEffect(() => {
    if (phase !== "results" || !summary || !patientName.trim() || readOnlyResults) {
      return;
    }

    const save = async () => {
      setSaveStatus("saving");
      try {
        await axios.post(
          `${CONFIG.BACKEND_URL}/backend/register-reaction-test`,
          {
            patient: patientName.trim(),
            trials,
            summary_metrics: summary,
          }
        );
        setSaveStatus("ok");
      } catch (err) {
        console.error("Error al guardar Go/No-Go:", err);
        setSaveStatus("error");
      }
    };

    save();
  }, [phase, summary, patientName, trials, readOnlyResults]);

  const goToMenu = () => {
    runningRef.current = false;
    testStartedRef.current = false;
    setPhase("menu");
    setPatientName("");
    setPatientError(false);
    setTrials([]);
    setSummary(null);
    setSaveStatus("idle");
    setReadOnlyResults(false);
    setSelectedIds(new Set());
  };

  const getRecordId = (row) => row._id || `${row.patient}-${row.testday}`;

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allHistorySelected =
    savedResults.length > 0 &&
    savedResults.every((row) => selectedIds.has(getRecordId(row)));

  const someHistorySelected =
    savedResults.some((row) => selectedIds.has(getRecordId(row))) &&
    !allHistorySelected;

  const toggleSelectAllHistory = () => {
    if (allHistorySelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(savedResults.map(getRecordId)));
    }
  };

  const handleDownloadSelected = () => {
    const selected = savedResults.filter((row) =>
      selectedIds.has(getRecordId(row))
    );
    if (selected.length === 0) return;
    downloadGonogoExcel(selected);
  };

  const handlePatientContinue = (e) => {
    e.preventDefault();
    if (!patientName.trim()) {
      setPatientError(true);
      return;
    }
    setPatientError(false);
    setPhase("instructions");
  };

  const handleStartTest = () => {
    runningRef.current = false;
    testStartedRef.current = false;
    setTrials([]);
    setSummary(null);
    setSaveStatus("idle");
    setReadOnlyResults(false);
    setTrialProgress({ current: 0, total: TOTAL_TRIALS });
    setPhase("test");
  };

  const handleRetry = () => {
    runningRef.current = false;
    testStartedRef.current = false;
    setTrials([]);
    setSummary(null);
    setSaveStatus("idle");
    setReadOnlyResults(false);
    setPhase("instructions");
  };

  const handleViewRecord = (record) => {
    setPatientName(record.patient);
    setTrials(record.trials || []);
    setSummary(record.summary_metrics);
    setReadOnlyResults(true);
    setSaveStatus("idle");
    setPhase("results");
  };

  return (
    <div className="gonogo-page">
      <header className="gonogo-topbar">
        <Link to="/" className="gonogo-topbar__brand">
          BANAS
        </Link>
      </header>

      {phase === "test" && (
        <div
          ref={testAreaRef}
          className="gonogo-test-area"
          tabIndex={0}
          role="application"
          aria-label="Área de la prueba Go/No-Go"
        >
          <span className="gonogo-progress">
            Ensayo {trialProgress.current} de {trialProgress.total}
          </span>
          {showFixation && <span className="gonogo-fixation">+</span>}
          {!showFixation && stimulus && (
            <div
              className={`gonogo-circle gonogo-circle--${stimulus}`}
              aria-hidden="true"
            />
          )}
          <span className="gonogo-hint">
            Presiona la barra espaciadora cuando corresponda
          </span>
        </div>
      )}

      {phase !== "test" && (
        <main className="gonogo-content">
          {phase === "menu" && (
            <div className="gonogo-card">
              <h1>Prueba Go / No-Go</h1>
              <p>Elige una opción para continuar.</p>
              <div className="gonogo-menu-options">
                <button
                  type="button"
                  className="gonogo-menu-option"
                  onClick={() => setPhase("patient")}
                >
                  <span className="gonogo-menu-option__title">Nuevo paciente</span>
                  <span className="gonogo-menu-option__desc">
                    Registrar nombre e iniciar una nueva prueba
                  </span>
                </button>
                <button
                  type="button"
                  className="gonogo-menu-option"
                  onClick={() => setPhase("history")}
                >
                  <span className="gonogo-menu-option__title">
                    Ver resultados
                  </span>
                  <span className="gonogo-menu-option__desc">
                    Consultar, seleccionar y descargar resultados en Excel
                  </span>
                </button>
              </div>
            </div>
          )}

          {phase === "patient" && (
            <div className="gonogo-card">
              <h1>Nuevo paciente</h1>
              <p>Indica el nombre del paciente antes de la prueba.</p>
              <Box
                component="form"
                onSubmit={handlePatientContinue}
                sx={{ mt: 2, maxWidth: 400 }}
              >
                <TextField
                  fullWidth
                  label="Nombre del paciente"
                  value={patientName}
                  onChange={(e) => {
                    setPatientName(e.target.value);
                    setPatientError(false);
                  }}
                  error={patientError}
                  helperText={patientError ? "El nombre es requerido" : ""}
                  sx={{ mb: 2 }}
                />
                <div className="gonogo-actions">
                  <Button type="button" variant="outlined" onClick={goToMenu}>
                    Volver al menú
                  </Button>
                  <Button type="submit" variant="contained" color="primary">
                    Continuar
                  </Button>
                </div>
              </Box>
            </div>
          )}

          {phase === "instructions" && (
            <div className="gonogo-card">
              <h1>Instrucciones</h1>
              <p>
                Paciente: <strong>{patientName.trim()}</strong>
              </p>
              <ul className="gonogo-instructions-list">
                <li>
                  Círculo{" "}
                  <span className="gonogo-color-demo gonogo-color-demo--green" />{" "}
                  <strong>verde</strong>: presiona <strong>espacio</strong> lo antes
                  posible (pasa al siguiente al instante).
                </li>
                <li>
                  Círculo{" "}
                  <span className="gonogo-color-demo gonogo-color-demo--red" />{" "}
                  <strong>rojo</strong>: no presiones. Si presionas espacio por
                  error, también avanzará al siguiente ensayo.
                </li>
                <li>
                  Si no respondes a un círculo verde, el ensayo terminará solo al
                  cabo de unos segundos.
                </li>
                <li>La prueba tiene {TOTAL_TRIALS} ensayos.</li>
              </ul>
              <div className="gonogo-actions">
                <Button variant="outlined" onClick={goToMenu}>
                  Volver al menú
                </Button>
                <Button variant="outlined" onClick={() => setPhase("patient")}>
                  Cambiar paciente
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleStartTest}
                >
                  Comenzar prueba
                </Button>
              </div>
            </div>
          )}

          {phase === "history" && (
            <div className="gonogo-card gonogo-card--wide">
              <h1>Resultados anteriores</h1>
              <p className="gonogo-history-hint">
                Selecciona las pruebas Go/No-Go que quieras y descárgalas en Excel.
              </p>
              <div className="gonogo-actions gonogo-history-toolbar">
                <Button variant="outlined" onClick={goToMenu}>
                  Volver al menú
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={selectedIds.size === 0}
                  onClick={handleDownloadSelected}
                >
                  Descargar Excel ({selectedIds.size})
                </Button>
              </div>

              {historyLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {!historyLoading && historyError && (
                <p className="gonogo-save-status gonogo-save-status--error">
                  {historyError}
                </p>
              )}

              {!historyLoading && !historyError && savedResults.length === 0 && (
                <p className="gonogo-empty-history">
                  Aún no hay pruebas guardadas.
                </p>
              )}

              {!historyLoading && savedResults.length > 0 && (
                <div className="gonogo-history-table-wrap">
                  <Table size="small" className="gonogo-history-table">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={someHistorySelected}
                            checked={allHistorySelected}
                            onChange={toggleSelectAllHistory}
                            inputProps={{ "aria-label": "Seleccionar todas" }}
                          />
                        </TableCell>
                        <TableCell>Paciente</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell align="right">Media RT (ms)</TableCell>
                        <TableCell align="right">Omisiones</TableCell>
                        <TableCell align="right">Correctas</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {savedResults.map((row) => {
                        const rowId = getRecordId(row);
                        const isSelected = selectedIds.has(rowId);
                        return (
                        <TableRow
                          key={rowId}
                          hover
                          selected={isSelected}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => toggleSelectRow(rowId)}
                              onClick={(e) => e.stopPropagation()}
                              inputProps={{ "aria-label": `Seleccionar ${row.patient}` }}
                            />
                          </TableCell>
                          <TableCell>{row.patient}</TableCell>
                          <TableCell>{formatDate(row.testday)}</TableCell>
                          <TableCell align="right">
                            {row.summary_metrics?.mean_reaction_time_ms ?? "—"}
                          </TableCell>
                          <TableCell align="right">
                            {row.summary_metrics?.omission_errors ?? "—"}
                          </TableCell>
                          <TableCell align="right">
                            {row.summary_metrics?.correct_hits ?? "—"}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleViewRecord(row)}
                            >
                              Ver
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          {phase === "results" && summary && (
            <ResultsPanel
              patientName={patientName.trim()}
              summary={summary}
              trials={trials}
              saveStatus={saveStatus}
              readOnly={readOnlyResults}
              onRetry={readOnlyResults ? undefined : handleRetry}
              onBackMenu={goToMenu}
            />
          )}
        </main>
      )}
    </div>
  );
}
